import type { IHelpdeskLimits, IHelpdeskMessageCreated } from "@kuika/kuika-cl-model";
import React, { useRef, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import HelpdeskDropzone from "./helpdesk-dropzone";
import { isAccountRestricted, readHelpdeskError } from "./helpdesk-errors";
import {
  addTotals,
  describeRejectedFile,
  IHelpdeskFileSelection,
  IHelpdeskFileTotals,
  IHelpdeskRejectedFile,
  selectFiles,
  totalsOf
} from "./helpdesk-files";
import HelpdeskPendingFiles from "./helpdesk-pending-files";
import { IHelpdeskPendingReply } from "./helpdesk-thread";
import {
  describeFailedUploads,
  IHelpdeskFailedUpload,
  IHelpdeskUploadProgress,
  newHelpdeskKey,
  uploadHelpdeskFiles
} from "./helpdesk-upload";
import { HELPDESK_UPLOAD_DEPS } from "./helpdesk-upload-deps";

export interface HelpdeskComposerProps {
  ticketNumber: number;
  limits: IHelpdeskLimits;
  /** What the ticket already holds; the ITSM caps a ticket at 100 files and 300 MB. */
  ticketFiles: IHelpdeskFileTotals;
  /**
   * The reply on its way, for the thread to show at once (and its progress as it goes); null when
   * sending failed and the reply went back into the box.
   */
  onPendingChange: (reply: IHelpdeskPendingReply | null) => void;
  /** The ITSM has the message (and its files); the thread re-reads to bring it in. */
  onSent: (messageId: number) => void;
  onRestricted: () => void;
}

/** Reply box of a ticket; only rendered when the ITSM says the person may reply (`canReply`). */
const HelpdeskComposer = ({
  ticketNumber,
  limits,
  ticketFiles,
  onPendingChange,
  onSent,
  onRestricted
}: HelpdeskComposerProps): React.ReactElement => {
  const [body, setBody] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState<boolean>(false);
  // One key per reply, renewed after it is sent: a retry of the same reply is replayed, not doubled.
  const replyKey: React.MutableRefObject<string> = useRef<string>(newHelpdeskKey());

  const isOverLimit: boolean = body.length > limits.descriptionMaxLength;
  const canSend: boolean = !sending && !isOverLimit && (body.trim().length > 0 || files.length > 0);

  const addFiles = (picked: File[]): void => {
    const selection: IHelpdeskFileSelection = selectFiles(picked, limits, addTotals(ticketFiles, totalsOf(files)));
    selection.rejected.forEach((rejected: IHelpdeskRejectedFile): void => {
      KMainFunctions.displayWarningNotification(describeRejectedFile(rejected, limits));
    });
    if (selection.accepted.length > 0) setFiles((current: File[]): File[] => [...current, ...selection.accepted]);
  };

  // The reply leaves the box the moment Send is pressed and shows in the thread as pending, the way
  // a chat does; if it cannot be sent it comes back here, so nothing typed is ever lost.
  const send = async (): Promise<void> => {
    if (!canSend) return;
    const draftBody: string = body;
    const draftFiles: File[] = files;
    let pending: IHelpdeskPendingReply = {
      body: draftBody,
      files: draftFiles,
      startedAt: new Date().toISOString(),
      messageId: null,
      progress: null
    };
    onPendingChange(pending);
    setBody("");
    setFiles([]);
    setSending(true);
    try {
      const created: IHelpdeskMessageCreated = await HelpdeskService.sendMessage(
        ticketNumber,
        { body: draftBody, withAttachments: draftFiles.length > 0 },
        replyKey.current
      );
      pending = { ...pending, messageId: created.messageId };
      onPendingChange(pending);
      const failed: IHelpdeskFailedUpload[] =
        draftFiles.length > 0
          ? await uploadHelpdeskFiles(
              ticketNumber,
              created.messageId,
              draftFiles,
              HELPDESK_UPLOAD_DEPS,
              (progress: IHelpdeskUploadProgress): void => onPendingChange({ ...pending, progress })
            )
          : [];
      if (failed.length > 0) {
        KMainFunctions.displayWarningNotification(
          `The message was sent, but these files could not be uploaded: ${describeFailedUploads(failed)}`
        );
      }
      replyKey.current = newHelpdeskKey();
      onSent(created.messageId);
    } catch (error) {
      // Not sent. The same idempotency key is kept, so sending it again cannot post it twice.
      onPendingChange(null);
      setBody(draftBody);
      setFiles(draftFiles);
      if (isAccountRestricted(error)) onRestricted();
      else KMainFunctions.displayErrorNotification(readHelpdeskError(error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="kuika-helpdesk-composer">
      <KuikaInput
        data-testid="currentTicketNewMessageInput"
        aria-label="Reply"
        type="textarea"
        rows={4}
        placeholder="Write a reply"
        value={body}
        disabled={sending}
        error={isOverLimit}
        onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>, value: string | number): void => setBody(String(value))}
      />
      <HelpdeskDropzone
        compact
        hint="Attach files"
        inputTestId="ticketReplyFileInput"
        disabled={sending}
        onFiles={addFiles}
      />
      <HelpdeskPendingFiles
        files={files}
        disabled={sending}
        onRemove={(removed: File): void =>
          setFiles((current: File[]): File[] => current.filter((f: File): boolean => f !== removed))
        }
      />
      <div className="kuika-flex-end--gap-s">
        <KuikaButton
          data-testid="currentTicketSendButton"
          type="primary"
          onClick={send}
          loading={sending}
          disabled={!canSend}
        >
          Send
        </KuikaButton>
      </div>
    </div>
  );
};

export default HelpdeskComposer;
